import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import { useSesion } from '../sesion/ProveedorDeSesion';

/**
 * Aceptar una invitación por enlace.
 *
 * Sirve para las dos clases que existen —sumarse a una empresa o fundar la propia— porque
 * para quien la recibe el paso es el mismo: poner su nombre, elegir una contraseña y
 * quedar adentro. Lo que cambia es dónde aterriza después, y eso lo resuelve el armazón.
 */
export function AceptarInvitacion({
  tipo,
}: {
  readonly tipo: 'empresa' | 'fundacion';
}): React.ReactElement {
  const { t } = useTranslation();
  const { entrar } = useSesion();
  const textoDeError = useTextoDeError();

  const token = new URLSearchParams(globalThis.location.search).get('token') ?? '';
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState<string | null>(null);

  const aceptar = useMutation({
    mutationFn: () =>
      pedir<{ tokenDeAcceso: string }>(
        tipo === 'empresa' ? '/invitaciones/aceptacion' : '/fundacion/aceptacion',
        {
          method: 'POST',
          cuerpo: { token, nombre, apellido: apellido === '' ? undefined : apellido, contrasena },
        },
      ),
    onSuccess: async (respuesta) => {
      await entrar(respuesta.tokenDeAcceso);
      // Se limpia el token de la barra de direcciones: queda en el historial y en el
      // registro del proxy, y ya no sirve para nada.
      globalThis.history.replaceState(null, '', '/');
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          aceptar.mutate();
        }}
        className="w-full max-w-sm rounded-marco border border-borde bg-superficie p-6"
      >
        <h1 className="mb-1 text-lg font-semibold tracking-tight">
          {tipo === 'empresa' ? t('invitacion.titulo') : t('invitacion.tituloFundacion')}
        </h1>
        <p className="mb-5 text-xs text-texto-tenue">
          {tipo === 'empresa' ? t('invitacion.subtitulo') : t('invitacion.subtituloFundacion')}
        </p>

        {token === '' ? (
          <p role="alert" className="text-sm text-mal">
            {t('invitacion.sinToken')}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <Campo
              etiqueta={t('invitacion.nombre')}
              obligatorio
              value={nombre}
              onChange={(evento) => {
                setNombre(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('invitacion.apellido')}
              value={apellido}
              onChange={(evento) => {
                setApellido(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('invitacion.contrasena')}
              type="password"
              autoComplete="new-password"
              obligatorio
              value={contrasena}
              ayuda={t('invitacion.ayudaContrasena')}
              onChange={(evento) => {
                setContrasena(evento.target.value);
              }}
            />
            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}
            <Boton type="submit" tono="principal" cargando={aceptar.isPending} className="w-full">
              {t('invitacion.aceptar')}
            </Boton>
          </div>
        )}
      </form>
    </div>
  );
}
